import type {
	ConversationChannel,
	ConversationChannelTask,
	TodoItem,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	type ConversationTask,
	type ExecutionSegment,
	type TodoSnapshot,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	mapApiTaskStatusToProgressState,
	mergeExecutionSegments,
} from '@atlassian/conversation-assistant-store/controllers/tasks-shared/task-helpers';

/**
 * Parses the raw `execution_segments` record from the API into an array
 * of `ExecutionSegment` objects, preserving arrival order.
 */
export const parseExecutionSegments = (
	raw: Record<string, string> | null | undefined,
): ExecutionSegment[] | null => {
	if (!raw) {
		return null;
	}
	try {
		return Object.values(raw).map((value) => {
			const parsed = JSON.parse(value);
			return {
				startedAt: parsed.startedAt,
				...(parsed.endedAt ? { endedAt: parsed.endedAt } : {}),
			};
		});
	} catch {
		return null;
	}
};

/**
 * Parses the raw `todo_list` record from the API into a `TodoSnapshot`.
 * Values are consumed in arrival order (Object.values preserves insertion order).
 */
export const parseTodoList = (
	raw: Record<string, string> | null | undefined,
): TodoSnapshot | undefined => {
	if (!raw) {
		return undefined;
	}
	try {
		const todos: TodoItem[] = Object.values(raw).map((value) => {
			const parsed = JSON.parse(value);
			return {
				id: parsed.id,
				content: parsed.content,
				status: parsed.status,
				priority: parsed.priority,
				active_form: parsed.activeForm ?? parsed.active_form,
			} satisfies TodoItem;
		});
		return { todos };
	} catch {
		return undefined;
	}
};

/**
 * Maps a single API task to the local domain ConversationTask, populating all
 * camelCase fields from the snake_case network object.
 */
const mapApiTaskToDomain = (apiTask: ConversationChannelTask): ConversationTask => ({
	id: apiTask.task_id,
	taskId: apiTask.task_id,
	progressState: mapApiTaskStatusToProgressState(apiTask.status),
	title: apiTask.title,
	messageId: apiTask.message_id,
	completionMessageId: apiTask.completion_message_id,
	currentMessageId: apiTask.current_message_id,
	updatedAt: apiTask.updated_at,
	conversationId: apiTask.conversation_id,
	agentId: apiTask.agent_id,
	triggerType: apiTask.trigger_type,
	plan: apiTask.plan,
	resultSummary: apiTask.result_summary,
	errorMessage: apiTask.error_message,
	createdAt: apiTask.created_at,
	startedAt: apiTask.started_at,
	completedAt: apiTask.completed_at,
	executionSegments: parseExecutionSegments(apiTask.execution_segments),
	todoSnapshot: parseTodoList(apiTask.todo_list),
});

/**
 * Merges remote tasks with local tasks.
 *
 * - Remote tasks are sorted by `created_at` ascending (oldest first, latest last).
 * - All tasks except the last one are fully overwritten from the network.
 * - For the last task: if its `task_id` matches the last local task's `id`,
 *   merge them — local `progressState`, `todoSnapshot` and local-only attributes
 *   (`seekingCompletedAck`, `id`) take precedence when local `updatedAt` is newer;
 *   otherwise network values are used.
 * - If the last network task does NOT match the last local task's `id`,
 *   overwrite from network and append the last local task on top.
 */
const mergeTasks = (
	remoteTasks: ConversationChannelTask[] | undefined,
	localTasks: ConversationTask[] | undefined,
): ConversationTask[] | undefined => {
	if (!remoteTasks || remoteTasks.length === 0) {
		return localTasks;
	}

	// Sort remote tasks by created_at ascending (oldest first)
	const sorted = [...remoteTasks].sort(
		(a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
	);

	const lastLocal =
		localTasks && localTasks.length > 0 ? localTasks[localTasks.length - 1] : undefined;
	const lastRemote = sorted[sorted.length - 1];

	// Map all remote tasks except the last one directly from network
	const result: ConversationTask[] = sorted.slice(0, -1).map(mapApiTaskToDomain);

	// Handle the last remote task — merge with last local if ids match
	if (lastLocal && lastRemote.task_id === lastLocal.id) {
		const localIsNewer = lastLocal.updatedAt && lastLocal.updatedAt > lastRemote.updated_at;

		const remoteDomain = mapApiTaskToDomain(lastRemote);
		result.push({
			...remoteDomain,
			// Preserve local-only attributes
			id: lastLocal.id,
			seekingCompletedAck: lastLocal.seekingCompletedAck,
			// Merge execution segments — remote wins on overlap, but retain unique local segments
			executionSegments: mergeExecutionSegments(
				lastLocal.executionSegments,
				remoteDomain.executionSegments,
			),
			// When local is newer, preserve local progressState and todoSnapshot
			...(localIsNewer
				? {
						progressState: lastLocal.progressState,
						todoSnapshot: lastLocal.todoSnapshot,
					}
				: {}),
		});
	} else {
		// No match — overwrite from network
		result.push(mapApiTaskToDomain(lastRemote));

		// Append last local task on top if it exists
		if (lastLocal) {
			result.push(lastLocal);
		}
	}

	return result;
};

/**
 * Merges conversation data from local state and API response with network priority.
 * Prioritizes fields from the API (channelsFromAPI) as they represent the latest server state.
 * Falls back to local state (localConversation) for fields missing in the API response.
 */
export const mergeConversationWithNetworkPriority = (
	remoteConversation: ConversationChannel,
	localConversation?: Conversation,
): Conversation => {
	return {
		id: remoteConversation.id,

		// Prioritize API data if present, otherwise use local or generate new
		name: remoteConversation.name ?? localConversation?.name ?? generateConversationName(),
		timeCreated: remoteConversation.time_created ?? localConversation?.timeCreated,
		lastMessage: remoteConversation.last_message ?? localConversation?.lastMessage ?? undefined,
		lastMessageType:
			remoteConversation.last_message_type ?? localConversation?.lastMessageType ?? [],
		selectedAgentIds: localConversation?.selectedAgentIds ?? [],
		lastMessageTimestamp:
			remoteConversation.last_message_timestamp ?? localConversation?.lastMessageTimestamp,
		lastAgentMessageStatus:
			remoteConversation.last_agent_message_status ?? localConversation?.lastAgentMessageStatus,
		readStatus: remoteConversation.readStatus ?? localConversation?.readStatus,
		tags: remoteConversation.tags ?? localConversation?.tags,

		// Use local state data if it exists (messages, status are maintained locally)
		messages: localConversation?.messages ?? [],
		status: localConversation?.status ?? CHAT_STORE_STATUS.idle,
		traces: localConversation?.traces ?? [],

		...(localConversation?.sourceAgentId ? { sourceAgentId: localConversation.sourceAgentId } : {}),
		...(localConversation?.sourceId ? { sourceId: localConversation.sourceId } : {}),
		// Preserve runtime abort controller so Stop button works after channels rehydration (e.g. refocus)
		...(localConversation?.storeAbortController != null
			? { storeAbortController: localConversation.storeAbortController }
			: {}),
		...(localConversation?.rovoSpaceId ? { rovoSpaceId: localConversation.rovoSpaceId } : {}),
		agent: remoteConversation.agent,
		scopes: remoteConversation.scopes ?? localConversation?.scopes,
		tasks: mergeTasks(remoteConversation.tasks, localConversation?.tasks),
	};
};
